import { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import { FileText, Plus, Upload, Send, Copy, Trash2, ChevronDown, ChevronUp, Eye, Share2, Check, PenLine } from 'lucide-react';

const STATUS_COLORS = {
  rascunho:  'bg-gray-100 text-gray-700',
  enviada:   'bg-blue-100 text-blue-700',
  aceita:    'bg-green-100 text-green-700',
  rejeitada: 'bg-red-100 text-red-700',
  expirada:  'bg-orange-100 text-orange-700',
  assinada:  'bg-emerald-100 text-emerald-700',
};

const STATUS_LABELS = {
  rascunho:  'Rascunho',
  enviada:   'Enviada',
  aceita:    'Aceita',
  rejeitada: 'Rejeitada',
  expirada:  'Expirada',
  assinada:  '✍️ Assinada',
};

function RequestSignatureModal({ proposal, onClose, onSent }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  async function handleSend(e) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError('');
    try {
      await api.requestProposalSignature(proposal.id, { signer_email: email });
      onSent();
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao enviar OTP');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-gray-100">
          <PenLine size={16} className="text-emerald-600" />
          <span className="font-semibold text-gray-800">Solicitar Assinatura Digital</span>
        </div>
        <form onSubmit={handleSend} className="p-5 space-y-3">
          <p className="text-xs text-gray-600">Um código OTP será enviado ao e-mail do prospect para confirmar a assinatura.</p>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">E-mail do signatário *</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="prospect@empresa.com"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
              autoFocus
            />
          </div>
          {error && <p className="text-xs text-red-500 bg-red-50 rounded px-3 py-2">{error}</p>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Cancelar</button>
            <button type="submit" disabled={sending || !email.trim()}
              className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 font-medium">
              {sending ? 'Enviando...' : 'Enviar código'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ProposalsTab({ lead }) {
  const toast = useToast();
  const [proposals, setProposals] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    valor_total: '',
    validade_dias: 15,
    observacoes: '',
    template_id: '',
    file: null,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shareLink, setShareLink] = useState({}); // proposalId -> url
  const [copied, setCopied] = useState(null);    // proposalId that was just copied
  const [signModal, setSignModal] = useState(null); // proposal to request signature for

  useEffect(() => {
    if (!lead?.id) return;
    load();
  }, [lead?.id]);

  async function load() {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api.getProposals(lead.id),
        api.getProposalTemplates(),
      ]);
      setProposals(p);
      setTemplates(t);
    } catch { /* silent */ }
    setLoading(false);
  }

  function handleTemplateChange(templateId) {
    const tmpl = templates.find(t => String(t.id) === String(templateId));
    setForm(f => ({
      ...f,
      template_id: templateId,
      title: tmpl ? tmpl.name : f.title,
      validade_dias: tmpl ? tmpl.default_validade_dias : f.validade_dias,
    }));
  }

  function handleFile(file) {
    if (!file) return;
    if (file.type !== 'application/pdf') {
      setError('Apenas arquivos PDF são aceitos.');
      return;
    }
    setError('');
    setForm(f => ({ ...f, file }));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) { setError('Título obrigatório'); return; }
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('lead_id', lead.id);
      fd.append('title', form.title);
      if (form.valor_total) fd.append('valor_total', form.valor_total);
      fd.append('validade_dias', form.validade_dias);
      if (form.observacoes) fd.append('observacoes', form.observacoes);
      if (form.template_id) fd.append('template_id', form.template_id);
      if (form.file) fd.append('file', form.file);
      await api.createProposal(fd);
      setShowForm(false);
      setForm({ title: '', valor_total: '', validade_dias: 15, observacoes: '', template_id: '', file: null });
      await load();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  }

  async function handleStatusChange(proposal, status) {
    try {
      await api.updateProposalStatus(proposal.id, status);
      await load();
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleDuplicate(proposal) {
    try {
      await api.duplicateProposal(proposal.id);
      await load();
      toast.success('Proposta duplicada');
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleShare(proposal) {
    try {
      const data = await api.shareProposal(proposal.id);
      setShareLink(prev => ({ ...prev, [proposal.id]: data.url }));
    } catch (err) {
      toast.error(err.message);
    }
  }

  async function handleCopyLink(proposalId, url) {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(proposalId);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // fallback
      prompt('Copie o link:', url);
    }
  }

  function formatCurrency(v) {
    if (!v) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
  }

  function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('pt-BR');
  }

  if (loading) {
    return <div className="p-4 text-sm text-gray-500">Carregando propostas...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Propostas ({proposals.length})</h3>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Nova Proposta
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <div className="border rounded-lg p-4 space-y-3 bg-gray-50">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Template</label>
              <select
                value={form.template_id}
                onChange={e => handleTemplateChange(e.target.value)}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="">— Sem template —</option>
                {templates.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Título *</label>
              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="Ex: Proposta ERP Totvs Moda — v1"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Valor Total</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.valor_total}
                onChange={e => setForm(f => ({ ...f, valor_total: e.target.value }))}
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Validade (dias)</label>
              <input
                type="number"
                min="1"
                value={form.validade_dias}
                onChange={e => setForm(f => ({ ...f, validade_dias: e.target.value }))}
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Observações</label>
              <textarea
                value={form.observacoes}
                onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))}
                rows={2}
                className="w-full border rounded px-2 py-1.5 text-sm resize-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Arquivo PDF</label>
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleDrop}
                onClick={() => fileRef.current?.click()}
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
                  dragging ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Upload className="w-5 h-5 mx-auto text-gray-400 mb-1" />
                {form.file ? (
                  <p className="text-sm text-gray-700">{form.file.name}</p>
                ) : (
                  <p className="text-sm text-gray-500">Arraste um PDF ou clique para selecionar</p>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={e => handleFile(e.target.files[0])}
                />
              </div>
            </div>
          </div>
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={() => { setShowForm(false); setError(''); }}
              className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {saving ? 'Salvando...' : 'Salvar Proposta'}
            </button>
          </div>
        </div>
      )}

      {/* Proposals list */}
      {proposals.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <FileText className="w-10 h-10 mx-auto mb-2 opacity-30" />
          <p className="text-sm">Nenhuma proposta ainda</p>
        </div>
      ) : (
        <div className="space-y-2">
          {proposals.map(p => (
            <div key={p.id} data-testid="proposal-card" className="border rounded-lg overflow-hidden">
              {/* Row */}
              <div className="flex items-center gap-3 px-3 py-2.5 bg-white hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-400">v{p.version}</span>
                    <span className="text-sm font-medium text-gray-800 truncate">{p.title}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[p.status]}`}>
                      {STATUS_LABELS[p.status]}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                    <span>{formatCurrency(p.valor_total)}</span>
                    <span>·</span>
                    <span>{formatDate(p.created_at)}</span>
                    {p.created_by_name && <><span>·</span><span>{p.created_by_name}</span></>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {p.file_path && (
                    <button
                      type="button"
                      onClick={() => setPreviewUrl(previewUrl === p.id ? null : p.id)}
                      className="p-1.5 text-gray-400 hover:text-blue-600 rounded"
                      title="Preview PDF"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleDuplicate(p)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 rounded"
                    title="Duplicar"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setExpandedId(expandedId === p.id ? null : p.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-700 rounded"
                  >
                    {expandedId === p.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Expanded actions */}
              {expandedId === p.id && (
                <div className="border-t px-3 py-2 bg-gray-50 flex flex-wrap gap-2">
                  {p.status === 'rascunho' && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(p, 'enviada')}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Marcar como Enviada
                      </button>
                    </>
                  )}
                  {p.status === 'enviada' && (
                    <>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(p, 'aceita')}
                        className="px-2.5 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                      >
                        Aceita
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(p, 'rejeitada')}
                        className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                      >
                        Rejeitada
                      </button>
                      <button
                        type="button"
                        onClick={() => handleStatusChange(p, 'expirada')}
                        className="px-2.5 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                      >
                        Expirada
                      </button>
                    </>
                  )}
                  {/* Solicitar assinatura — disponível quando enviada ou rascunho */}
                  {p.status !== 'assinada' && (
                    <button
                      type="button"
                      onClick={() => setSignModal(p)}
                      className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-emerald-700 border border-emerald-200 rounded hover:bg-emerald-50"
                      title="Enviar código OTP para assinatura digital"
                    >
                      <PenLine className="w-3.5 h-3.5" />
                      Solicitar Assinatura
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => handleShare(p)}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-purple-700 border border-purple-200 rounded hover:bg-purple-50"
                    title="Gerar link para o cliente"
                  >
                    <Share2 className="w-3.5 h-3.5" />
                    Compartilhar
                  </button>

                  {shareLink[p.id] && (
                    <div className="w-full flex items-center gap-2 mt-1 bg-white border border-purple-200 rounded-lg px-3 py-1.5">
                      <span className="flex-1 text-xs text-gray-600 truncate">{shareLink[p.id]}</span>
                      <button
                        type="button"
                        onClick={() => handleCopyLink(p.id, shareLink[p.id])}
                        className="shrink-0 text-purple-600 hover:text-purple-800"
                        title="Copiar link"
                      >
                        {copied === p.id ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  )}

                  {p.observacoes && (
                    <p className="w-full text-xs text-gray-600 mt-1">{p.observacoes}</p>
                  )}
                </div>
              )}

              {/* PDF preview */}
              {previewUrl === p.id && p.file_path && (
                <div className="border-t">
                  <iframe
                    src={`/uploads/${p.file_path}`}
                    title="PDF Preview"
                    className="w-full h-96"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {signModal && (
        <RequestSignatureModal
          proposal={signModal}
          onClose={() => setSignModal(null)}
          onSent={() => { setSignModal(null); load(); }}
        />
      )}
    </div>
  );
}
